import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import { toast } from "react-toastify";
import api from "../../services/api";

const ImpersonationContext = createContext(null);

const STORAGE_KEY = "impersonation_data";
const TIMEOUT_MS = 2 * 60 * 60 * 1000; // 2 horas

export const ImpersonationProvider = ({ children }) => {
  const [isImpersonating, setIsImpersonating] = useState(false);
  const [targetCompany, setTargetCompany] = useState(null);
  const [logId, setLogId] = useState(null);
  const [startedAt, setStartedAt] = useState(null);
  const timeoutRef = useRef(null);

  const endImpersonation = useCallback(async (skipApi = false) => {
    const saved = JSON.parse(localStorage.getItem(STORAGE_KEY) || "null");
    if (!saved) return;

    try {
      if (!skipApi && saved.logId) {
        await api.post("/impersonation/end", { logId: saved.logId });
      }
    } catch (_) {}

    // Restaurar token original
    if (saved.originalToken) {
      localStorage.setItem("token", JSON.stringify(saved.originalToken));
      api.defaults.headers.Authorization = "Bearer " + saved.originalToken;
    }

    localStorage.removeItem(STORAGE_KEY);
    if (timeoutRef.current) clearTimeout(timeoutRef.current);

    setIsImpersonating(false);
    setTargetCompany(null);
    setLogId(null);
    setStartedAt(null);

    // Recarregar para limpar o estado do app completamente
    window.location.replace("/tickets");
  }, []);

  const startImpersonation = useCallback(async (targetCompanyId, reason) => {
    const originalToken = JSON.parse(localStorage.getItem("token") || "null");
    const { data } = await api.post("/impersonation/start", { targetCompanyId, reason });

    const impData = {
      originalToken,
      impersonationToken: data.access_token,
      logId: data.log_id,
      targetCompany: data.company,
      startedAt: new Date().toISOString()
    };

    localStorage.setItem(STORAGE_KEY, JSON.stringify(impData));
    localStorage.setItem("token", JSON.stringify(data.access_token));
    api.defaults.headers.Authorization = "Bearer " + data.access_token;

    setIsImpersonating(true);
    setTargetCompany(data.company);
    setLogId(data.log_id);
    setStartedAt(impData.startedAt);

    // Timeout automático de 2h
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => {
      toast.warn("Sessão de suporte expirada após 2 horas.");
      endImpersonation(false);
    }, TIMEOUT_MS);

    // Recarregar — o refresh_token no backend detecta o token de impersonation
    // e retorna o contexto correto da empresa alvo
    window.location.replace("/tickets");
  }, [endImpersonation]);

  // Restaurar estado ao carregar a página
  useEffect(() => {
    const saved = JSON.parse(localStorage.getItem(STORAGE_KEY) || "null");
    if (!saved) return;

    const elapsed = Date.now() - new Date(saved.startedAt).getTime();
    if (elapsed >= TIMEOUT_MS) {
      endImpersonation(false);
      return;
    }

    setIsImpersonating(true);
    setTargetCompany(saved.targetCompany);
    setLogId(saved.logId);
    setStartedAt(saved.startedAt);

    timeoutRef.current = setTimeout(() => {
      toast.warn("Sessão de suporte expirada após 2 horas.");
      endImpersonation(false);
    }, TIMEOUT_MS - elapsed);

    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <ImpersonationContext.Provider value={{
      isImpersonating,
      targetCompany,
      logId,
      startedAt,
      startImpersonation,
      endImpersonation
    }}>
      {children}
    </ImpersonationContext.Provider>
  );
};

export const useImpersonation = () => {
  const ctx = useContext(ImpersonationContext);
  if (!ctx) throw new Error("useImpersonation must be used inside ImpersonationProvider");
  return ctx;
};

export default ImpersonationContext;
